import { theme } from 'antd';

import type { MappingAlgorithm } from 'antd';

const overridePresetShades: MappingAlgorithm = (_seedToken, mapToken) =>
  ({
    ...mapToken,
    green1: '#DAECE3',
    green2: '#C9E4D6',
    green3: '#B5D9C6',
    green6: '#1C3829',
    greenHover: '#1C3829',
    greenActive: '#1C3829',
    blue1: '#D3E5EF',
    blue2: '#BCD4E2',
    blue3: '#A5C3D5',
    blue6: '#183347',
    blueHover: '#183347',
    blueActive: '#183347',
    purple1: '#E8DEEE',
    purple2: '#D5C5DE',
    purple3: '#C2ACCE',
    purple6: '#492960',
    purpleHover: '#492960',
    purpleActive: '#492960',
    orange1: '#FADEC9',
    orange2: '#F5C7A8',
    orange3: '#F0B187',
    orange6: '#5C3B23',
    orangeHover: '#5C3B23',
    orangeActive: '#5C3B23',
    yellow1: '#FDECC8',
    yellow2: '#F5DFA8',
    yellow3: '#EDD288',
    yellow6: '#703B00',
    yellowHover: '#703B00',
    yellowActive: '#703B00',
    blue5: '#2B5EBE',
    blue7: '#2B5EBE',
    cyan5: '#007C77',
    cyan7: '#007C77',
    green5: '#1C7848',
    green7: '#1C7848',
    yellow5: '#8A6118',
    yellow7: '#8A6118',
    orange5: '#B05A1A',
    orange7: '#B05A1A',
    purple5: '#5E40BA',
    purple7: '#5E40BA',
    pink5: '#B23678',
    pink7: '#B23678',
    red5: '#BA3434',
    red7: '#BA3434',
    geekblue5: '#3F3F9E',
    geekblue7: '#3F3F9E',
    lime5: '#5F7A1F',
    lime7: '#5F7A1F',
    volcano5: '#855A3B',
    volcano7: '#855A3B',

    colorPrimary: '#2D2D32',
    colorPrimaryHover: '#44444A',
    colorPrimaryActive: '#18181B',

    colorSuccess: '#1C7848',
    colorSuccessHover: '#228854',
    colorSuccessActive: '#15653C',
    colorSuccessBg: '#E9F2ED',
    colorSuccessBgHover: '#DCEAE3',
    colorSuccessBorder: '#C3DDD0',
    colorSuccessText: '#1C7848',
    colorSuccessTextHover: '#228854',
    colorSuccessTextActive: '#15653C',

    colorError: '#BA3434',
    colorErrorHover: '#C94545',
    colorErrorActive: '#A32B2B',
    colorErrorBg: '#F8EBEB',
    colorErrorBgHover: '#F3DDDD',
    colorErrorBorder: '#E7C2C2',
    colorErrorText: '#BA3434',
    colorErrorTextHover: '#C94545',
    colorErrorTextActive: '#A32B2B',

    colorWarning: '#8A6118',
    colorWarningHover: '#9E7122',
    colorWarningActive: '#735012',
    colorWarningBg: '#F5F0E5',
    colorWarningBgHover: '#EFE7D5',
    colorWarningBorder: '#DFD0AE',
    colorWarningText: '#8A6118',
    colorWarningTextHover: '#9E7122',
    colorWarningTextActive: '#735012',

    colorInfo: '#2B5EBE',
    colorInfoHover: '#3C6ECB',
    colorInfoActive: '#224EA3',
    colorInfoBg: '#EAEFF9',
    colorInfoBgHover: '#DCE5F5',
    colorInfoBorder: '#C2D1EE',
    colorInfoText: '#24749E',
    colorInfoTextHover: '#2F86B2',
    colorInfoTextActive: '#1C6288',
  }) as ReturnType<MappingAlgorithm>;

export const lightTheme = {
  algorithm: [theme.defaultAlgorithm, overridePresetShades],
  cssVar: { prefix: 'ant' },
  token: {
    fontFamily: "'Inter Variable', Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    fontFamilyCode: "'JetBrains Mono Variable', 'JetBrains Mono', monospace",

    colorBgBase: '#FFFFFF',
    colorTextBase: '#18181B',

    colorLink: '#2D2D32',
    linkDecoration: 'underline',
    linkHoverDecoration: 'underline',

    colorText: '#18181B',
    colorTextSecondary: '#3F3F46',
    colorTextTertiary: '#71717A',
    colorTextQuaternary: '#A1A1AA',
    colorTextPlaceholder: '#A1A1AA',
    colorTextLightSolid: '#FFFFFF',
    colorWhite: '#FFFFFF',

    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgLayout: '#FAFAFA',
    colorBgSpotlight: '#18181B',
    colorBgMask: 'rgba(9, 9, 11, 0.35)',
    colorFillAlter: '#FAFAFA',

    colorBorder: '#DEDEE0',
    colorBorderSecondary: '#EEEEF0',
    colorSplit: '#F2F2F4',

    colorFill: 'rgba(9, 9, 11, 0.10)',
    colorFillSecondary: 'rgba(9, 9, 11, 0.055)',
    colorFillTertiary: 'rgba(9, 9, 11, 0.05)',
    colorFillQuaternary: 'rgba(9, 9, 11, 0.03)',

    boxShadowTertiary: '0 1px 1px rgba(9, 9, 11, 0.04)',
    boxShadowSecondary: '0 12px 32px rgba(9, 9, 11, 0.12)',
    boxShadow: '0 12px 32px rgba(9, 9, 11, 0.18)',

    lineWidthFocus: 0,
  },
  components: {
    Button: {
      fontWeight: 500,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      cyan6: '#007C77',
      cyanHover: '#008E88',
      cyanActive: '#00615D',
    },
    Dropdown: { borderRadiusLG: 16, borderRadiusSM: 12 },
    Popover: { borderRadiusLG: 16 },
    Tooltip: { colorBgSpotlight: '#18181B', colorTextLightSolid: '#FFFFFF' },
    Input: { colorFillTertiary: '#F4F4F5', colorFillSecondary: '#F4F4F5', activeBg: '#F4F4F5' },
    InputNumber: {
      colorFillTertiary: '#F4F4F5',
      colorFillSecondary: '#F4F4F5',
      activeBg: '#F4F4F5',
    },
    Mentions: { colorFillTertiary: '#F4F4F5', colorFillSecondary: '#F4F4F5', activeBg: '#F4F4F5' },
    DatePicker: {
      colorFillTertiary: '#F4F4F5',
      colorFillSecondary: '#F4F4F5',
      activeBg: '#F4F4F5',
    },
    Select: {
      colorFillTertiary: '#F4F4F5',
      colorFillSecondary: '#F4F4F5',
      borderRadiusLG: 16,
      borderRadiusSM: 12,
    },
    TreeSelect: { colorFillTertiary: '#F4F4F5', colorFillSecondary: '#F4F4F5' },
    Cascader: { colorFillTertiary: '#F4F4F5', colorFillSecondary: '#F4F4F5' },
  },
};
